import { useParams, useNavigate } from 'react-router-dom';
import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  useColorModeValue,
  Heading,
  Button,
  HStack,
  Flex,
  VStack,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { apiUpdateBinderChecklist } from 'api';
import type { APIBinderData } from 'types/api.types';
import {
  CollapsibleSection,
  StickyNavigation,
  ReportHeader,
  GaragingAddressCard,
  LeaseVehicleCard,
  PleasureUseCard,
  BusinessUseCard,
  AnnualDrivingDistanceCard,
  CarPoolingCard,
  TypeOfFuelCard,
  UnrepairedDamageCard,
  ModifiedCustomizedCard,
  ExceptionalUsageCard,
  MaritalStatusCard,
  DateFirstLicensedCard,
  GraduatedLicensingCard,
  PrincipalDriverAssignmentCard,
  UnlistedDriversCard,
  UnlistedVehiclesCard,
  SuspensionsCancellationsCard,
  ClaimsCard,
  ConvictionsCard,
  LapsesCard,
  PolicyChangeFormsCard,
  RemarksCard,
  MVRLicenseStatusCard,
  ValidationCards,
  UploadSummaryCard,
  QuoteDetailsCard,
} from './components';
import { useBinderSectionData } from './hooks/useBinderSectionData';
import GapInInsuranceImmediatelyPrior from './components/GapInInsuranceImmediatelyPrior';
import MultiLineDiscount from './components/MultiLineDiscount';
import UnknownOperatorClaimsCard from './components/UnknownOperatorClaimsCard';

const SECTION_REGISTRY = [
  { id: 'address', component: GaragingAddressCard },
  { id: 'leased-vehicle', component: LeaseVehicleCard },
  { id: 'pleasure-use', component: PleasureUseCard },
  { id: 'business-use', component: BusinessUseCard },
  {
    id: 'annual-driving-distance',
    component: AnnualDrivingDistanceCard,
  },
  { id: 'car-pooling', component: CarPoolingCard },
  { id: 'type-of-fuel', component: TypeOfFuelCard },
  { id: 'unrepaired-damage', component: UnrepairedDamageCard },
  { id: 'modified-customized', component: ModifiedCustomizedCard },
  { id: 'exceptional-usage', component: ExceptionalUsageCard },
  { id: 'marital-status', component: MaritalStatusCard },
  { id: 'date-first-licensed', component: DateFirstLicensedCard },
  { id: 'graduated-licensing', component: GraduatedLicensingCard },
  {
    id: 'principal-driver-assignment',
    component: PrincipalDriverAssignmentCard,
  },
  {
    id: 'suspensions-6-years',
    component: SuspensionsCancellationsCard,
  },
  {
    id: 'cancellations-3-years',
    component: SuspensionsCancellationsCard,
  },
  {
    id: 'misrepresentation-3-years',
    component: SuspensionsCancellationsCard,
  },
  {
    id: 'gap-in-insurance-immediately-prior',
    component: GapInInsuranceImmediatelyPrior,
  },
  {
    id: 'multi-line-discount',
    component: MultiLineDiscount,
  },
  { id: 'mvr-license-status', component: MVRLicenseStatusCard },
  { id: 'unlisted-vehicles', component: UnlistedVehiclesCard },
  { id: 'unlisted-drivers', component: UnlistedDriversCard },
  { id: 'claims', component: ClaimsCard },
  {
    id: 'unknown-operator-claims',
    component: UnknownOperatorClaimsCard,
  },
  { id: 'convictions', component: ConvictionsCard },
  { id: 'lapses', component: LapsesCard },
  { id: 'quote-details', component: QuoteDetailsCard },
  { id: 'policy-change-forms', component: PolicyChangeFormsCard },
];

interface AutoBinderReportViewProps {
  data: APIBinderData | null;
}

const AutoBinderReportView = ({
  data: binderData,
}: AutoBinderReportViewProps) => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState<string>('');
  const [highlightTriggers, setHighlightTriggers] = useState<
    Record<string, number>
  >({});

  const bgColor = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  const sectionData = useBinderSectionData(binderData);

  const reportSections = useMemo(
    () =>
      SECTION_REGISTRY.filter((section) => sectionData[section.id])
        .sort((a, b) => {
          const aIsValid = sectionData[a.id]?.isValid;

          return aIsValid ? 1 : -1;
        })
        .map((section) => ({
          id: section.id,
          label:
            sectionData[section.id]?.title ||
            section.id
              .replace(/-/g, ' ')
              .replace(/\b\w/g, (l) => l.toUpperCase()),
          isValid: sectionData[section.id]?.isValid,
        })),
    [sectionData],
  );

  const filteredSections = useMemo(
    () =>
      SECTION_REGISTRY.filter((section) => sectionData[section.id]).sort(
        (a, b) => {
          const aIsValid = sectionData[a.id]?.isValid;

          return aIsValid ? 1 : -1;
        },
      ),
    [sectionData],
  );

  const handleNavigateToOverview = useCallback(() => {
    navigate(`/admin/policies/overview/${uuid}`);
  }, [navigate, uuid]);

  const handleSectionNavigation = useCallback((sectionId: string) => {
    setHighlightTriggers((prev) => ({
      ...prev,
      [sectionId]: (prev[sectionId] || 0) + 1,
    }));
  }, []);

  const handleChecklistUpdate = useCallback(
    async (checklistData: {
      [validationId: string]: { checked: boolean };
    }): Promise<boolean> => {
      if (!uuid) return false;

      try {
        await apiUpdateBinderChecklist(uuid, checklistData);
        queryClient.invalidateQueries({ queryKey: ['binder-report', uuid] });
        return true;
      } catch (error) {
        console.error('Error updating binder checklist:', error);
        return false;
      }
    },
    [uuid, queryClient],
  );

  return (
    <Card bg={bgColor} p={8}>
      {/* Header with navigation */}
      <Box borderColor={borderColor}>
        <Flex justify="space-between" align="center" mb={6}>
          <Box>
            <Heading size="md" color={textColor}>
              Binder Report
            </Heading>
          </Box>
          <HStack spacing={3}>
            <Button
              variant="outline"
              colorScheme="teal"
              onClick={handleNavigateToOverview}
              size="sm"
            >
              Binder Overview
            </Button>
          </HStack>
        </Flex>

        {/* Report Header Information */}
        <ReportHeader applicationData={binderData?.application_data} />
      </Box>

      {/* Sticky Navigation */}
      <Box>
        <StickyNavigation
          sections={reportSections}
          activeSection={activeSection}
          onActiveChange={setActiveSection}
          onNavigate={handleSectionNavigation}
        />

        {/* Report Sections */}
        <VStack spacing={4} align="stretch">
          <ValidationCards
            validations={binderData?.validation_data?.validations || []}
            checklistData={binderData?.checklist_data?.validations || {}}
            onChecklistUpdate={handleChecklistUpdate}
            applicant={binderData?.application_data?.applicant}
          />

          {filteredSections.map((section) => {
            const SectionComponent = section.component;
            const data = sectionData[section.id];

            return (
              <CollapsibleSection
                key={section.id}
                id={section.id}
                title={data?.title}
                isActive={activeSection === section.id}
                highlightTrigger={highlightTriggers[section.id] || 0}
                isValid={data?.isValid}
                message={data?.message}
              >
                <SectionComponent
                  data={data?.data}
                  sectionData={
                    section.id === 'policy-change-forms' ? data : data?.data
                  }
                />
              </CollapsibleSection>
            );
          })}

          {/* Keep original sections temporarily for comparison - will remove later */}
          <UploadSummaryCard
            sectionData={sectionData['upload-summary']?.data}
          />

          <RemarksCard sectionData={sectionData['remarks']} />
        </VStack>
      </Box>
    </Card>
  );
};

export default AutoBinderReportView;
